import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AddRulesProctoringComponent } from '../add-rules-proctoring/add-rules-proctoring.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ProctoringService } from '@edtech-ui/core/services/proctoring-dashboard/proctoring.service';
import { CreateProctoringRulesetPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/create-proctoring-ruleset';
import { ToastrService } from 'ngx-toastr';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, Subscription, tap } from 'rxjs';
import { GetProctoringRuleset, GetProctoringRulesetsPagedPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctoring-rulesets';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProctoringDeleteConfigComponent } from '../proctoring-delete-config/proctoring-delete-config.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { GetProctoringConfigurationsPagedRequest } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctroing-config';
import { UpdateProctoringConfigurationPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/update-proctoring-config';


@Component({
	selector: 'app-proctoring-ruleset',
	standalone: true,
	imports: [CommonModule, FormsModule, RouterModule, MatDialogModule, MatProgressSpinnerModule, MatTooltipModule,
		HasPermissionDirective
	],
	templateUrl: './proctoring-ruleset.component.html',
	styleUrl: './proctoring-ruleset.component.scss'
})
export class ProctoringRulesetComponent implements OnInit, OnDestroy {

	searchText: string = '';
	showColumnDropdown = false;
	currentPage = 1;
	totalPages = 0
	fromDate: string = '';
	toDate: string = '';
	isLoading = false;
	isInitialLoad = true;
	subscription = new Subscription();
	ruleData: GetProctoringRuleset[] = [];
	pageSize = 10;
	total = 0;
	sortField = '';
	sortOrder: 'asc' | 'desc' = 'desc';
	searchSubject = new Subject<string>();
	companyId = '';
	PermissionForStudents = PermissionForStudents;
	configData: any[] = [];
	defaultConfiguration: any = null;
	selectedConfigurationId = '';
	defaultRuleSetId: string | null = null;


	constructor(
		private dialog: MatDialog,
		private router: Router,
		private proctoringService: ProctoringService,
		private toastr: ToastrService
	) { }


	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';
		this.subscription.add(
			this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
				this.currentPage = 1;
				this.getProctoringRuleset();
			})
		);

		this.getProctoringRuleset();
		this.getProctoringConfiguration();
	}

	openRuleSet() {
		const dialogRef = this.dialog.open(AddRulesProctoringComponent, {
			width: '911px',
			enterAnimationDuration: '0ms',
			exitAnimationDuration: '0ms',
			panelClass: 'overflow-visible-dialog'
		});

		dialogRef.afterClosed().subscribe((
			result: any
		) => {
			if (!result) return;

			this.submitRuleset(result.payload, result.selectedConfig, result.isDefault, result.actualDefaultConfig);
		})
	}


	goToRulesetPage(ruleSetId: string) {
		this.router.navigate(['/proctoring-dashboard/ruleset', ruleSetId])
	}


	goToRulesetEditPage(ruleSetId: string) {
		this.router.navigate(['/proctoring-dashboard/ruleset', ruleSetId], { queryParams: { edit: true } })
	};


	submitRuleset(payload: CreateProctoringRulesetPayload, selectedConfig?: any, isDefault?: boolean, actualDefaultConfig?: any) {
		this.subscription.add(
			this.proctoringService.postProctoringRuleset(payload).pipe(
				tap((response) => {
					if (response.success) {
						this.toastr.success(response.message);
						if (response.data?.id) {
							const configToUpdate = (isDefault && actualDefaultConfig) ? actualDefaultConfig : selectedConfig;
							
							if (configToUpdate) {
								this.updateProctoringConfig(configToUpdate, response.data.id).subscribe(() => {
									this.getProctoringConfiguration();
									this.getProctoringRuleset();
								});
							} else {
								this.getProctoringRuleset();
							}
						}

						this.sortField = '';
						this.sortOrder = 'desc';
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	getProctoringRuleset() {
		this.isLoading = true;

		const payload: GetProctoringRulesetsPagedPayload = {
			page: this.currentPage,
			pageSize: this.pageSize,
			filters: {
				companyId: {
					equals: this.companyId
				}
			},
			sort: [
				{
					field: this.sortField || 'createdAt',
					order: (this.sortOrder ? this.sortOrder.toUpperCase() : 'DESC') as 'ASC' | 'DESC'
				}
			],
			all: false
		};

		if (this.searchText && this.searchText.trim() !== '') {
			payload.search = this.searchText;
		}

		this.subscription.add(
			this.proctoringService.getProctoringRuleset(payload).pipe(
				tap((response) => {
					if (response.success) {
						this.ruleData = response.data.data;
						this.markDefaultRuleset();
						this.total = response.data.pagination.total;
						this.currentPage = response.data.pagination.page;
						this.pageSize = response.data.pagination.pageSize;
						this.totalPages = response.data.pagination.totalPages;
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
					this.isInitialLoad = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	get startRecord(): number {
		return this.total === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
	}


	get endRecord(): number {
		return Math.min(this.currentPage * this.pageSize, this.total);
	}


	changePageSize() {
		this.currentPage = 1;
		this.getProctoringRuleset();
	}


	previousPage() {
		if (this.currentPage > 1) {
			this.currentPage--;
			this.getProctoringRuleset();
		}
	}


	onSearch() {
		this.currentPage = 1;
		this.getProctoringRuleset();
	}


	nextPage() {
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.getProctoringRuleset();
		}
	}

	goToPage(page: number) {
		if (page >= 1 && page <= this.totalPages) {
			this.currentPage = page;
			this.getProctoringRuleset();
		}
	}

	get pagesArray(): number[] {
		const pages = this.totalPages || Math.ceil(this.total / (this.pageSize || 10)) || 1;
		return Array.from({ length: pages }, (_, i) => i + 1);
	}

	get displayTotalPages(): number {
		return this.totalPages || Math.ceil(this.total / (this.pageSize || 10)) || 1;
	}


	sortBy(field: string) {
		if (this.sortField === field) {
			this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortField = field;
			this.sortOrder = 'asc';
		}

		this.currentPage = 1;
		this.getProctoringRuleset();
	}


	onSearchChange() {
		this.searchSubject.next(this.searchText.trim());
	}


	deleteProctoringRuleset(rulesetId: string) {
		this.subscription.add(
			this.proctoringService.deleteProctoringRuleset(rulesetId).pipe(
				tap(response => {
					if (response.success) {
						this.toastr.success(response.message);
						this.sortField = '';
						this.sortOrder = 'desc';
						this.getProctoringRuleset();
					} else {
						this.toastr.error(response.message);
					}
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	openDeleteDialogBox(rulesetId: string, name: string) {
		const dialogRef = this.dialog.open(ProctoringDeleteConfigComponent, {
			data: {
				name,
				type: 'ruleset'
			}
		});

		dialogRef.afterClosed().subscribe(result => {
			if (!result) {
				return;
			}
			this.deleteProctoringRuleset(rulesetId);
		});
	}


	getProctoringConfiguration() {
		this.isLoading = true;
		const payload: GetProctoringConfigurationsPagedRequest = {
			page: this.currentPage,
			pageSize: this.pageSize,
			filters: {
				companyId: {
					equals: this.companyId
				},
				isDefault: {
					equals: true
				}
			},
			sort: [
				{
					field: 'createdAt',
					order: 'DESC'
				}
			],
			all: false
		};
		this.subscription.add(
			this.proctoringService.getProctoringConfiguration(payload).pipe(
				tap(response => {
					if (response.success) {
						this.configData = response.data.data;
						this.markDefaultRuleset();
						this.total = response.data.pagination.total;
						this.currentPage = response.data.pagination.page;
						this.pageSize = response.data.pagination.pageSize
						this.totalPages = response.data.pagination.totalPages;
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		)
	}


	private markDefaultRuleset(): void {
		if (!this.ruleData.length || !this.configData.length) {
			return;
		}

		const defaultConfig = this.configData.find(config => config.isDefault && config.defaultRuleSetId);
		this.defaultRuleSetId = defaultConfig?.defaultRuleSetId ?? null;
		this.ruleData = this.ruleData.map(rule => ({ ...rule, isDefault: rule.id === this.defaultRuleSetId }));
	}

	updateProctoringConfig(selectedConfig: any, ruleSetId: string) {
		const updateConfigPayload: UpdateProctoringConfigurationPayload = {
			companyId: selectedConfig.companyId,
			name: selectedConfig.name,
			description: selectedConfig.description,
			isEnabled: selectedConfig.isEnabled,
			isDefault: selectedConfig.isDefault,
			allowedBrowsers: selectedConfig.allowedBrowsers,
			allowedDevices: selectedConfig.allowedDevices,
			requireCamera: selectedConfig.requireCamera,
			requireMicrophone: selectedConfig.requireMicrophone,
			requireScreenShare: selectedConfig.requireScreenShare,
			requireFullscreen: selectedConfig.requireFullscreen,
			recordCamera: selectedConfig.recordCamera,
			recordScreen: selectedConfig.recordScreen,
			recordAudio: selectedConfig.recordAudio,
			maxMedium: selectedConfig.maxMedium,
			maxHigh: selectedConfig.maxHigh,
			maxCritical: selectedConfig.maxCritical,
			defaultRuleSetId: ruleSetId
		};

		return this.proctoringService.updateProctoringConfigById(selectedConfig.configId, updateConfigPayload);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}